import React from "react";
import { Link } from "gatsby";
import { Menu } from "@headlessui/react";
import { FaChevronDown } from "react-icons/fa/";

import ThemeToggle from "./ThemeToggle";
import Logos from "./Logos";
import { PulseDot } from "./LiveScoreBadge";
import { logos } from "../util/logos";
import { getTeamURL, slugifgyTeamName } from "../util/urlUtil";
import { useTeams } from "../hooks/useTeams";
import { useAllMatches } from "../hooks/useAllMatches";
import { useLiveMatches } from "../hooks/useLiveMatches";
import { getLiveCandidates, liveMatchKey } from "../util/liveMatchUtil";
import { seasons, currentSeasonId } from "../util/seasons";

const StyledLink = ({ path, children, target }) => {
  return (
    <li className="list-none">
      <Link
        to={path}
        className="font-display whitespace-nowrap text-xs font-medium uppercase tracking-wide text-white no-underline xl:text-[13px] hover:border-0 hover:border-b-4 hover:border-solid hover:border-gold hover:pb-3"
        activeClassName="border-0 border-b-4 border-solid border-gold pb-3"
        target={target}
      >
        {children}
      </Link>
    </li>
  );
};

const Navbar = ({ theme, toggleTheme }) => {
  const teams = useTeams();
  const sorted = teams.sort((a, b) => a.fullName.localeCompare(b.fullName));

  const { allMatches } = useAllMatches();
  const liveCandidates = getLiveCandidates(allMatches);
  const liveResults = useLiveMatches(liveCandidates);
  const liveTeamSlugs = new Set();
  liveCandidates.forEach((match) => {
    if (liveResults[liveMatchKey(match)]?.status === "IN_PROGRESS") {
      liveTeamSlugs.add(match.homeSlug);
      liveTeamSlugs.add(match.awaySlug);
    }
  });

  return (
    <div className="hidden lg:flex flex-col relative">
      <div className="flex w-full items-center justify-center h-12 bg-paper dark:bg-navy">
        {sorted.map((team) => {
          return (
            <Link
              key={team.slug}
              className="relative mx-1 flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-violet-tint dark:hover:bg-white/10"
              to={getTeamURL(team.fullName)}
            >
              <img
                width="%100px"
                height="%100px"
                src={logos[slugifgyTeamName(team.fullName)]}
                alt={team.fullName}
              />
              {liveTeamSlugs.has(team.slug) && (
                <span className="absolute -right-0.5 -top-0.5">
                  <PulseDot />
                </span>
              )}
            </Link>
          );
        })}
      </div>
      <Logos />
      <ul className="lg:flex lg:my-0 lg:h-16 lg:w-full lg:items-center lg:bg-violet lg:pl-40 lg:pr-8">
        <div className="flex flex-1 items-center justify-center gap-8">
          <StyledLink path="/">Ana Sayfa</StyledLink>
          <StyledLink path="/sezon-ozeti/">Sezon Özeti</StyledLink>
          <StyledLink path="/haftanin-panoramasi/">
            Haftanın Panoraması
          </StyledLink>
          <StyledLink path="/takim-istatistikleri/">
            Takım İstatistikleri
          </StyledLink>
          <StyledLink path="/fikstur/">Fikstür ve Sonuçlar</StyledLink>
          <StyledLink path="/puan-durumu/">Puan Durumu</StyledLink>
          <StyledLink path="/lig-liderleri/">Lig Liderleri</StyledLink>
          <StyledLink path="/hakemler/">Hakemler</StyledLink>
        </div>
        <div className="flex items-center gap-6">
          <Menu as="div" className="relative list-none">
            <Menu.Button className="font-display flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-white xl:text-[13px] hover:border-0 hover:border-b-4 hover:border-solid hover:border-gold hover:pb-3">
              Sezonlar
              <FaChevronDown className="w-2 h-2" />
            </Menu.Button>
            <Menu.Items className="absolute right-0 mt-2 w-40 rounded bg-white dark:bg-surface-dark shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white dark:ring-opacity-10 focus:outline-none z-30">
              {seasons.map((season) =>
                season.id === currentSeasonId ? (
                  <div
                    key={season.id}
                    className="flex items-center gap-2 px-4 py-2 text-xs xl:text-sm font-semibold text-gold"
                  >
                    {season.label}
                  </div>
                ) : (
                  <Menu.Item key={season.id}>
                    {({ active }) => (
                      <a
                        href={season.url}
                        target="_blank"
                        rel="noreferrer"
                        className={`block px-4 py-2 text-xs xl:text-sm text-gray-900 dark:text-white ${
                          active ? "bg-violet-tint dark:bg-white/10" : ""
                        }`}
                      >
                        {season.label}
                      </a>
                    )}
                  </Menu.Item>
                )
              )}
            </Menu.Items>
          </Menu>

          <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
        </div>
      </ul>
    </div>
  );
};

export default Navbar;
